import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Project } from '@/types/project';
import { useAuth } from '../useAuth';
import { dbLogger } from '@/lib/structuredLogger';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';
import { MAINTENANCE_PROJECTS_KEY } from '@/lib/queryKeys/maintenanceKeys';
import type { LifecycleStage, LifecycleWatchSubstatus } from '@/constants/lifecycleStages';

// RPC response type for get_maintenance_projects_optimized
interface MaintenanceRPCResult {
  success: boolean;
  data: {
    projects: Project[];
    total_count: number;
  };
  error?: {
    message: string;
  };
}

export interface MaintenanceFilters {
  searchQuery: string;
  projectGroup: string;
  projectType: string[];
  teamLead: string;
  company: string;
  projectMark?: string;
  projectRoleUserIds?: string[];
  projectRoleTypes?: string[];
  lifecycleStage?: LifecycleStage | '';
  watchSubstatus?: LifecycleWatchSubstatus | '';
  handoverDateFrom?: string; // YYYY-MM-DD
  handoverDateTo?: string;   // YYYY-MM-DD
  contractYear?: string[];
}



interface MaintenanceProjectResponse {
  projects: Project[];
  totalCount: number;
  executionTime: number;
}

// page-independent key — page change does NOT trigger refetch
const buildQueryKey = (filters: MaintenanceFilters) => [
  MAINTENANCE_PROJECTS_KEY,
  filters.searchQuery || '',
  filters.projectGroup || '',
  (Array.isArray(filters.projectType) ? filters.projectType : []).join(',') || '',
  filters.teamLead || '',
  filters.company || '',
  filters.projectMark || '',
  (filters.projectRoleUserIds || []).join(',') || '',
  (filters.projectRoleTypes || []).join(',') || '',
  filters.lifecycleStage || '',
  filters.watchSubstatus || '',
  filters.handoverDateFrom || '',
  filters.handoverDateTo || '',
  (filters.contractYear || []).join(',') || '',
];


// RPC caps at 100 (validation `Must be 1-100`).
// Current: ~70 Service projects → 100 enough.
// If scaling toward 100, raise RPC cap via DB migration.
const FETCH_ALL_LIMIT = 100;

export const useMaintenanceProjectsRPC = (
  filters: MaintenanceFilters,
  page: number = 1,
  pageSize: number = 50
) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: buildQueryKey(filters),
    queryFn: () => fetchMaintenanceProjects(filters),
    enabled: !!user?.id,
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    placeholderData: keepPreviousData,
    select: (full): MaintenanceProjectResponse => {
      const start = (page - 1) * pageSize;
      return {
        projects: full.projects.slice(start, start + pageSize),
        totalCount: full.totalCount,
        executionTime: full.executionTime,
      };
    },
  });
};

const fetchMaintenanceProjects = async (
  filters: MaintenanceFilters
): Promise<MaintenanceProjectResponse> => {
  const startTime = performance.now();

  try {
    const { data, error } = await supabase.rpc('get_maintenance_projects_optimized', {
      search_query: filters.searchQuery || null,
      project_group_filter: filters.projectGroup || null,
      project_type_filter: Array.isArray(filters.projectType) && filters.projectType.length > 0 ? filters.projectType[0] : null,
      team_lead_filter: filters.teamLead || null,
      company_filter: filters.company || null,
      page_offset: 0,
      page_limit: FETCH_ALL_LIMIT,
      contract_year_filter: filters.contractYear?.length ? filters.contractYear : null,
    });


    if (error) {
      dbLogger.error('Maintenance RPC failed', { error: error.message });
      throw error;
    }

    const result = data as unknown as MaintenanceRPCResult;
    const queryTime = performance.now() - startTime;

    if (!result.success) {
      dbLogger.warn('Maintenance RPC returned error', { error: result.error });
      throw new Error(result.error?.message || 'RPC error');
    }

    let projects: Project[] = result.data.projects || [];

    if (projects.length === 100) {
      console.warn(
        '[useMaintenanceProjectsRPC] Hit page_limit cap 100. ' +
          'Service projects approaching cap — consider raising RPC cap or pagination strategy.'
      );
    }

    // Client-side filter: project_mark
    if (filters.projectMark) {
      projects = projects.filter((p) => p.project_mark === filters.projectMark);
    }

    // Client-side filter: project_role_types
    if (filters.projectRoleTypes && filters.projectRoleTypes.length > 0) {
      const { data: roleTypeProjectIds } = await supabase
        .from('project_user_permissions')
        .select('project_id')
        .overlaps('project_roles', filters.projectRoleTypes);
      const idSet = new Set((roleTypeProjectIds || []).map((p) => p.project_id));
      projects = projects.filter((p) => idSet.has(p.id));
    }

    // Client-side filter: project_role_user_ids
    if (filters.projectRoleUserIds && filters.projectRoleUserIds.length > 0) {
      const userProjectIds = await chunkedInQuerySimple<{ project_id: string }>(
        supabase,
        'project_user_permissions',
        'user_id',
        filters.projectRoleUserIds,
        'project_id',
        (query) => query.not('project_roles', 'eq', '{}')
      );
      const idSet = new Set(userProjectIds.map((p) => p.project_id));
      projects = projects.filter((p) => idSet.has(p.id));
    }

    // Client-side filter: lifecycle_stage
    if (filters.lifecycleStage) {
      projects = projects.filter((p) => p.lifecycle_stage === filters.lifecycleStage);
    }

    // Client-side filter: watch_substatus (only meaningful when stage='watch')
    // null/undefined watch_substatus is treated as 'theo_doi' to match display merge
    if (filters.watchSubstatus) {
      projects = projects.filter(
        (p) => ((p as any).watch_substatus ?? 'theo_doi') === filters.watchSubstatus,
      );
    }



    // Client-side filter: handover_date range
    const from = filters.handoverDateFrom || '';
    const to = filters.handoverDateTo || '';
    if (from || to) {
      projects = projects.filter((p) => {
        const hd = p.handover_date;
        if (!hd) return false; // NULL excluded when any bound set
        if (from && hd < from) return false;
        if (to && hd > to) return false;
        return true;
      });
    }

    const totalCount = projects.length;

    dbLogger.info('Maintenance projects query completed', {
      totalCount,
      projectsReturned: projects.length,
      queryTime,
      filters,
    });

    return {
      projects,
      totalCount,
      executionTime: queryTime,
    };
  } catch (error) {
    dbLogger.error('Maintenance query failed', { error });
    throw error;
  }
};
